/**
 * Journey local suggestion engine (`is_journey_by_api: false`).
 *
 * Isolated on purpose — tweak the TUNABLE CONSTANTS block below without
 * touching wizard UI, API clients, or JourneyResult chrome.
 *
 * Pipeline:
 * 1) User needs (BMR → TDEE → target kcal + daily macros)
 * 2) Plan daily output + match penalty
 * 3) Tiered price-desc sort — full catalog always kept (no hard excludes)
 */
import {
  resolveCatalogPlanCalorieRange,
  resolveCatalogPlanExplicitPortion,
  resolveCatalogPlanScaledMacros,
  normalizePlanCalorieLimit,
} from '../subscription/catalog/subscriptionScaledMacros';
import { resolvePlanOfferPricing } from '../subscription/catalog/subscriptionPlanPrice';
import type { PlanItem } from '../subscription/catalog/subscriptionData';
import type { JourneyGoal } from './config';

// ─── TUNABLE CONSTANTS ───────────────────────────────────────────────────────

/** Mifflin–St Jeor activity multipliers (TDEE = BMR × factor). */
export const JOURNEY_LOCAL_ACTIVITY_FACTORS = {
  sedentary: 1.2,
  lightly_active: 1.375,
  moderately_active: 1.55,
  active: 1.725,
  very_active: 1.9,
} as const;

/** Goal calorie adjustments applied to TDEE. */
export const JOURNEY_LOCAL_CALORIE_GOAL_FACTORS = {
  /** 20% deficit */
  loss: 0.8,
  /** maintenance */
  between: 1.0,
  /** 15% surplus */
  gain: 1.15,
} as const;

/** Target protein grams per kg body weight by goal. */
export const JOURNEY_LOCAL_PROTEIN_G_PER_KG = {
  loss: 2.0,
  between: 1.6,
  gain: 1.8,
} as const;

/**
 * Share of target calories allocated to carbs (protein is absolute g/kg;
 * remaining calories after protein+carbs are treated as fat — not scored).
 * Loss = lower carb, gain = higher carb, maintain = balanced.
 */
export const JOURNEY_LOCAL_CARB_CALORIE_SHARE = {
  loss: 0.35,
  between: 0.45,
  gain: 0.5,
} as const;

/** 1 portion ≈ this many grams of scaled carb/protein base. */
export const JOURNEY_LOCAL_PORTION_GRAMS = 100;

/** Default snack scaled grams when plan has no snack-specific macros (1 portion). */
export const JOURNEY_LOCAL_SNACK_DEFAULT_GRAMS = 100;

/** Penalty weights — calories prioritized over macros. */
export const JOURNEY_LOCAL_WEIGHT_CAL = 1.0;
export const JOURNEY_LOCAL_WEIGHT_MACRO = 0.5;

/** Hard filter: reject plans whose range misses target by more than this (kcal). */
export const JOURNEY_LOCAL_CALORIE_SHIELD_KCAL = 400;

/** Tier boundaries on total penalty score. */
export const JOURNEY_LOCAL_TIER1_MAX_PENALTY = 150;
export const JOURNEY_LOCAL_TIER2_MAX_PENALTY = 350;

// ─── Types ───────────────────────────────────────────────────────────────────

export type JourneyLocalActivityId = keyof typeof JOURNEY_LOCAL_ACTIVITY_FACTORS;

export type JourneyLocalGoal = Exclude<JourneyGoal, ''>;

export type JourneyLocalUserInput = {
  goal: JourneyLocalGoal;
  gender: 'male' | 'female' | string;
  age: number;
  height_cm: number;
  weight_kg: number;
  activity: JourneyLocalActivityId | string;
};

export type JourneyUserNeeds = {
  bmr: number;
  tdee: number;
  target_calories: number;
  target_protein_g: number;
  target_carbs_g: number;
  activity_id: JourneyLocalActivityId;
  goal: JourneyLocalGoal;
};

export type JourneyPlanDailyOutput = {
  min_calories: number | null;
  max_calories: number | null;
  avg_calories: number | null;
  daily_protein_g: number | null;
  daily_carbs_g: number | null;
  meals: number;
  snacks: number;
  price: number;
};

export type JourneyPlanMatch = {
  item: PlanItem;
  penalty: number;
  delta_cal: number;
  delta_macro: number;
  tier: 1 | 2 | 3;
  daily: JourneyPlanDailyOutput;
};

// ─── Activity / goal normalization ───────────────────────────────────────────

const ACTIVITY_ALIASES: Record<string, JourneyLocalActivityId> = {
  sedentary: 'sedentary',
  lightly_active: 'lightly_active',
  'lightly active': 'lightly_active',
  moderately_active: 'moderately_active',
  'moderately active': 'moderately_active',
  active: 'active',
  very_active: 'very_active',
  'very active': 'very_active',
};

export function normalizeJourneyLocalActivity(raw: string | null | undefined): JourneyLocalActivityId {
  if (!raw) {
    return 'moderately_active';
  }
  const key = String(raw).trim().toLowerCase().replace(/-/g, '_');
  return ACTIVITY_ALIASES[key] ?? ACTIVITY_ALIASES[key.replace(/_/g, ' ')] ?? 'moderately_active';
}

export function normalizeJourneyLocalGoal(raw: string | null | undefined): JourneyLocalGoal {
  if (raw === 'loss' || raw === 'gain' || raw === 'between') {
    return raw;
  }
  return 'between';
}

// ─── Step 1: user needs ──────────────────────────────────────────────────────

/** Mifflin–St Jeor BMR (kg, cm, years). */
export function calculateJourneyBmr(input: {
  gender: string;
  weight_kg: number;
  height_cm: number;
  age: number;
}): number {
  const weight = Number(input.weight_kg);
  const height = Number(input.height_cm);
  const age = Number(input.age);
  const base = 10 * weight + 6.25 * height - 5 * age;
  const is_female = String(input.gender).toLowerCase() === 'female';
  return is_female ? base - 161 : base + 5;
}

export function calculateJourneyTdee(bmr: number, activity: JourneyLocalActivityId): number {
  return bmr * JOURNEY_LOCAL_ACTIVITY_FACTORS[activity];
}

/**
 * Full Step-1 result: BMR, TDEE, target daily calories + protein/carbs grams.
 * Safe to call from submit or result ranking.
 */
export function computeJourneyUserNeeds(input: JourneyLocalUserInput): JourneyUserNeeds {
  const goal = normalizeJourneyLocalGoal(input.goal);
  const activity_id = normalizeJourneyLocalActivity(input.activity);
  const weight_kg = Math.max(1, Number(input.weight_kg) || 1);
  const height_cm = Math.max(1, Number(input.height_cm) || 1);
  const age = Math.max(1, Number(input.age) || 1);

  const bmr = calculateJourneyBmr({
    gender: input.gender,
    weight_kg,
    height_cm,
    age,
  });
  const tdee = calculateJourneyTdee(bmr, activity_id);
  const target_calories = Math.round(tdee * JOURNEY_LOCAL_CALORIE_GOAL_FACTORS[goal]);
  const target_protein_g = Math.round(weight_kg * JOURNEY_LOCAL_PROTEIN_G_PER_KG[goal]);
  const target_carbs_g = Math.round((target_calories * JOURNEY_LOCAL_CARB_CALORIE_SHARE[goal]) / 4);

  return {
    bmr: Math.round(bmr),
    tdee: Math.round(tdee),
    target_calories,
    target_protein_g,
    target_carbs_g,
    activity_id,
    goal,
  };
}

// ─── Step 2: plan daily output + penalty ─────────────────────────────────────

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function firstPositive(...candidates: unknown[]): number | null {
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') {
      continue;
    }
    const n = typeof candidate === 'number' ? candidate : Number(candidate);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  return null;
}

function resolvePlanMealSnackCounts(plan: unknown): { meals: number; snacks: number } {
  const root = asRecord(plan) ?? {};
  const detail = asRecord(root.detail) ?? {};
  const meals =
    firstPositive(
      root.number_of_meals,
      root.total_meals,
      root.number_of_meal,
      root.meal_count,
      detail.number_of_meals,
      detail.total_meals,
    ) ?? 0;
  const snacks =
    firstPositive(
      root.number_of_snacks,
      root.total_snacks,
      root.snack_count,
      detail.number_of_snacks,
      detail.total_snacks,
    ) ?? 0;
  return { meals, snacks };
}

/**
 * Per-meal scaled grams: prefer scaled_* / protein|carbs; else portion × PORTION_GRAMS.
 * Snacks default to JOURNEY_LOCAL_SNACK_DEFAULT_GRAMS when unspecified.
 */
export function resolvePlanDailyMacros(plan: unknown): {
  daily_protein_g: number | null;
  daily_carbs_g: number | null;
  meals: number;
  snacks: number;
} {
  const { meals, snacks } = resolvePlanMealSnackCounts(plan);
  const scaled = resolveCatalogPlanScaledMacros(plan);
  const portion = resolveCatalogPlanExplicitPortion(plan);
  const portion_grams =
    portion != null && portion > 0 ? portion * JOURNEY_LOCAL_PORTION_GRAMS : null;

  const meal_protein = scaled.scaled_protein ?? portion_grams;
  const meal_carbs = scaled.scaled_carbs ?? portion_grams;

  // Snacks: fixed 100g base (1 portion) when the plan includes snacks.
  const snack_protein = snacks > 0 ? JOURNEY_LOCAL_SNACK_DEFAULT_GRAMS : 0;
  const snack_carbs = snack_protein;

  if (meal_protein == null && meal_carbs == null) {
    return { daily_protein_g: null, daily_carbs_g: null, meals, snacks };
  }

  const daily_protein_g =
    meal_protein != null ? meal_protein * meals + snack_protein * snacks : null;
  const daily_carbs_g = meal_carbs != null ? meal_carbs * meals + snack_carbs * snacks : null;

  return { daily_protein_g, daily_carbs_g, meals, snacks };
}

export function resolvePlanDailyOutput(item: PlanItem): JourneyPlanDailyOutput {
  const range = resolveCatalogPlanCalorieRange(item.plan);
  const min_raw = normalizePlanCalorieLimit(range.min_calories);
  const max_raw = normalizePlanCalorieLimit(range.max_calories);
  const min_calories = min_raw > 0 ? min_raw : null;
  const max_calories = max_raw > 0 ? max_raw : null;
  const avg_calories =
    min_calories != null && max_calories != null
      ? (min_calories + max_calories) / 2
      : min_calories ?? max_calories;

  const macros = resolvePlanDailyMacros(item.plan);
  const price = resolvePlanOfferPricing(item.plan as Record<string, unknown>).displayPrice;

  return {
    min_calories,
    max_calories,
    avg_calories,
    daily_protein_g: macros.daily_protein_g,
    daily_carbs_g: macros.daily_carbs_g,
    meals: macros.meals,
    snacks: macros.snacks,
    price,
  };
}

export function calculatePlanMatchPenalty(
  needs: JourneyUserNeeds,
  daily: JourneyPlanDailyOutput,
): { delta_cal: number; delta_macro: number; penalty: number } {
  const target = needs.target_calories;
  let delta_cal = 0;
  if (daily.min_calories != null && daily.max_calories != null) {
    if (target >= daily.min_calories && target <= daily.max_calories) {
      delta_cal = 0;
    } else {
      delta_cal = Math.min(Math.abs(target - daily.min_calories), Math.abs(target - daily.max_calories));
    }
  } else if (daily.avg_calories != null) {
    delta_cal = Math.abs(target - daily.avg_calories);
  } else {
    // No calorie data — heavy penalty so these sink to tier 3 / stay last.
    delta_cal = JOURNEY_LOCAL_CALORIE_SHIELD_KCAL * 2;
  }

  let delta_macro = 0;
  if (daily.daily_protein_g != null || daily.daily_carbs_g != null) {
    const p = daily.daily_protein_g != null ? Math.abs(daily.daily_protein_g - needs.target_protein_g) : 0;
    const c = daily.daily_carbs_g != null ? Math.abs(daily.daily_carbs_g - needs.target_carbs_g) : 0;
    delta_macro = p + c;
  }

  const penalty = JOURNEY_LOCAL_WEIGHT_CAL * delta_cal + JOURNEY_LOCAL_WEIGHT_MACRO * delta_macro;
  return { delta_cal, delta_macro, penalty };
}

// ─── Step 3: tiered price sort (never drop plans) ────────────────────────────

/**
 * Soft shield helper — kept for fine-tuning / diagnostics.
 * Local ranking does **not** exclude plans; poor fits sink to tier 3 via penalty.
 */
export function passesJourneyCalorieShield(needs: JourneyUserNeeds, daily: JourneyPlanDailyOutput): boolean {
  const target = needs.target_calories;
  const shield = JOURNEY_LOCAL_CALORIE_SHIELD_KCAL;
  if (daily.max_calories != null && daily.max_calories < target - shield) {
    return false;
  }
  if (daily.min_calories != null && daily.min_calories > target + shield) {
    return false;
  }
  return true;
}

function penaltyToTier(penalty: number): 1 | 2 | 3 {
  if (penalty <= JOURNEY_LOCAL_TIER1_MAX_PENALTY) {
    return 1;
  }
  if (penalty <= JOURNEY_LOCAL_TIER2_MAX_PENALTY) {
    return 2;
  }
  return 3;
}

function sortByPriceDesc(a: JourneyPlanMatch, b: JourneyPlanMatch): number {
  return b.daily.price - a.daily.price;
}

/**
 * Score every plan and return Tier1 → Tier2 → Tier3
 * (each tier sorted price high → low).
 * Never filters — the full catalog always comes back, only reordered.
 */
export function rankPlanItemsForJourney(items: PlanItem[], needs: JourneyUserNeeds): PlanItem[] {
  const tier1: JourneyPlanMatch[] = [];
  const tier2: JourneyPlanMatch[] = [];
  const tier3: JourneyPlanMatch[] = [];

  for (const item of items) {
    const daily = resolvePlanDailyOutput(item);
    const { delta_cal, delta_macro, penalty } = calculatePlanMatchPenalty(needs, daily);
    // Soft shield: calorie outliers always land in tier 3 (still listed).
    const tier: 1 | 2 | 3 = passesJourneyCalorieShield(needs, daily) ? penaltyToTier(penalty) : 3;
    const match: JourneyPlanMatch = { item, penalty, delta_cal, delta_macro, tier, daily };
    if (tier === 1) {
      tier1.push(match);
    } else if (tier === 2) {
      tier2.push(match);
    } else {
      tier3.push(match);
    }
  }

  tier1.sort(sortByPriceDesc);
  tier2.sort(sortByPriceDesc);
  tier3.sort(sortByPriceDesc);

  return [...tier1, ...tier2, ...tier3].map(m => m.item);
}

/**
 * Convenience: build needs from wizard-ish fields and rank catalog PlanItems.
 */
export function suggestJourneyPlanItems(
  items: PlanItem[],
  input: JourneyLocalUserInput,
): { needs: JourneyUserNeeds; items: PlanItem[] } {
  const needs = computeJourneyUserNeeds(input);
  return { needs, items: rankPlanItemsForJourney(items, needs) };
}
